import { get } from 'lodash';
import { match } from 'ts-pattern';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

import {
  DateValuePicker,
  FormItem,
  PasswordInput as LuterkortPasswordInput,
  TextInput as LuterkortTextInput,
  TextareaPrimitive,
} from '@ingrid/luterkort-react';

import { CountrySelect } from '~src/components/CountrySelect/CountrySelect';
import { CurrencySelect } from '~src/components/CurrencySelect/CurrencySelect';
import { useTranslation } from '~src/i18n/use-translation';
import { toLocalCalendarDate } from '~src/utils/internationalized-date';

import { ValidateSchema } from '../../create/validate';
import { DynamicPath, useCarrierContractFormContext } from '../../types';
import { useUISchema } from '../hooks';
import { Schema } from '../queries';
import { SchemaFormLabel } from './schema-form-label';
import { SchemaFormSubText } from './schema-form-sub-text';

interface Props {
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  schema: Schema;
  schemaId: string;
  path?: string;
}

function DateInput({ schemaId, schema, disabled, defaultValue, readOnly, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId, schema)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <DateValuePicker
          // Due to runtime types (defined by the schema) we need to do some type casting
          // since typescript doesn't really understand this natively.
          defaultValue={value ? toLocalCalendarDate(value as unknown as string) : undefined}
          onChange={value => onChange(value!.toString())}
          isInvalid={fieldState.invalid}
          isReadOnly={readOnly}
          isDisabled={disabled}
          {...field}
        />
      )}
    />
  );
}

function CurrencyInput({ schemaId, schema, disabled, defaultValue, readOnly, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId, schema)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <CurrencySelect
          // Due to runtime types (defined by the schema) we need to do some type casting
          // since typescript doesn't really understand this natively.
          value={value as unknown as string | undefined}
          disabled={disabled ?? readOnly}
          invalid={fieldState.invalid}
          onChange={v => onChange(v)}
          {...field}
        />
      )}
    />
  );
}

function CountryInput({ schemaId, schema, disabled, defaultValue, readOnly, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId, schema)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <CountrySelect
          // Due to runtime types (defined by the schema) we need to do some type casting
          // since typescript doesn't really understand this natively.
          value={value as unknown as string | undefined}
          onChange={value => onChange(value)}
          disabled={disabled ?? readOnly}
          invalid={fieldState.invalid}
          {...field}
        />
      )}
    />
  );
}

function PasswordInput({ schemaId, schema, disabled, defaultValue, readOnly, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId, schema)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <LuterkortPasswordInput
          value={value as unknown as string | undefined}
          onChange={value => onChange(value)}
          data-invalid={fieldState.invalid}
          disabled={disabled}
          readOnly={readOnly}
          {...field}
        />
      )}
    />
  );
}

function TextInput({ schemaId, schema, disabled, defaultValue, readOnly, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId, schema)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <LuterkortTextInput
          value={value as unknown as string | undefined}
          onChange={value => onChange(value)}
          data-invalid={fieldState.invalid}
          readOnly={readOnly}
          disabled={disabled}
          {...field}
        />
      )}
    />
  );
}

function EulaInput({ schemaId, schema, disabled, defaultValue, readOnly, path = '' }: Props) {
  const { control, trigger } = useCarrierContractFormContext();
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  useEffect(() => {
    const el = textareaRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      setHasScrolledToBottom(true);
    }
  }, []);

  useEffect(() => {
    if (hasScrolledToBottom && path) {
      void trigger(path as DynamicPath);
    }
  }, [hasScrolledToBottom, path, trigger]);

  const handleScroll = useCallback(() => {
    const el = textareaRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 2) {
      setHasScrolledToBottom(true);
    }
  }, []);

  return (
    <Controller
      rules={{
        ...ValidateSchema(path, schemaId, schema),
        validate: () => hasScrolledToBottom || t('carrierContracts.details.validateEulaScrollRequired'),
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ref, ...field }, fieldState }) => {
        const composedRef = (node: HTMLTextAreaElement | null) => {
          textareaRef.current = node;
          if (typeof ref === 'function') ref(node);
        };

        return (
          <TextareaPrimitive
            ref={composedRef}
            value={value as unknown as string | undefined}
            style={{ height: '400px', resize: 'none' }}
            onChange={value => onChange(value)}
            onScroll={handleScroll}
            data-invalid={fieldState.invalid}
            readOnly={readOnly}
            disabled={disabled}
            {...field}
          />
        );
      }}
    />
  );
}

function StringInput(props: Props) {
  const { widget, defaultValue, isReadonly } = useUISchema({ path: props.path ?? '', schemaId: props.schemaId });

  const Component = match(widget)
    .with('password', () => PasswordInput)
    .with('country', () => CountryInput)
    .with('date', () => DateInput)
    .with('currency', () => CurrencyInput)
    .with('eula', () => EulaInput)
    .otherwise(() => TextInput);

  // eslint-disable-next-line react-hooks/static-components
  return <Component {...props} defaultValue={defaultValue as string | undefined} readOnly={isReadonly} />;
}

export function SchemaFormString({ schema, schemaId, disabled, path = '' }: Props) {
  const { formState } = useCarrierContractFormContext();

  return (
    <FormItem.Root>
      <SchemaFormLabel schema={schema} path={path} schemaId={schemaId} />

      <FormItem.Control invalid={!!get(formState.errors, path)?.message}>
        <StringInput schema={schema} path={path} schemaId={schemaId} disabled={disabled} />
      </FormItem.Control>

      <SchemaFormSubText schema={schema} path={path} />
    </FormItem.Root>
  );
}
